<script>
    import Button from "../../../Components/Common/Button.svelte";
    import CustomTooltip from "../../../Components/Common/CustomTooltip.svelte";
    import { RuleType } from "../../../Utils/utilEnums.svelte";

    export let encounterDetails;
    export let reason;
    export let difference = null;
    export let applyQuoteRule = null;
    export let restoreDefaultInvoiceRule = null;

    export let billingObj = null;
    export let mode = 0; //0 = full topup refund details, 1 = only apply quote button

    $: quoteData = encounterDetails.billingData.find(b => b.isLatestQuoteData);
    $: invoiceData = encounterDetails.billingData.find(b => b.isLatestInvoiceData);

    $: hasPaidPayment = hasPaymentAndItIsPaid(); 

    $: hideApplyQuoteButton = false; //quoteData.billingType == 3 && !quoteData.patientPaysTotalFee;
    $: hideMatchQuoteButton = quoteData != null && quoteData.totalFee == 0;

    $: sameRuleAsOnQuoteApplied = quoteData != null && billingObj != null && quoteData.billingType == billingObj.type && quoteData.calculationAmount == billingObj.amount;

    $: disableApplyQuoteButton = sameRuleAsOnQuoteApplied || (quoteData != null && quoteData.patientPaysTotalFee && quoteData.billingType == 5 && quoteData.totalFee < invoiceData.rebate); 
    $: disableMatchQuoteButton = quoteData != null && quoteData.patientPaysTotalFee && quoteData.billingType == 5 && quoteData.totalFee < invoiceData.rebate;


    const hasPaymentAndItIsPaid = () => {
        const quoteBillingData = encounterDetails.billingData.find(b => b.isLatestQuoteData);
        if(quoteBillingData && quoteBillingData.id){
            //get the estimate
            if(encounterDetails.estimates.some(e => e.patientEncounterBillingDataId == quoteBillingData.id)){
                var estimate = encounterDetails.estimates.find(e => e.patientEncounterBillingDataId == quoteBillingData.id);
                if(estimate.payments && estimate.payments.length > 0) {
                    var payment = estimate.payments[0];
                    return payment.status == 1 || payment.status == 2;
                }
            }
        }

        return false;
    }

    const sumOfPaidPayments = () => {
        const quoteBillingData = encounterDetails.billingData.find(b => b.isLatestQuoteData);
        if(quoteBillingData && quoteBillingData.id){
            //get the estimate
            if(encounterDetails.estimates.some(e => e.patientEncounterBillingDataId == quoteBillingData.id)){
                var estimate = encounterDetails.estimates.find(e => e.patientEncounterBillingDataId == quoteBillingData.id);
                if(estimate.payments && estimate.payments.length > 0) {
                    //sum all payments received, with payment.status 1 and 2
                    return estimate.payments
                        .filter(p => p.status === 1 || p.status === 2)
                        .reduce((sum, p) => sum + (p.amount ?? 0), 0);
                }
            }
        }

        return 0;
    }

    const getApplyQuoteTooltip = () => {
        if(sameRuleAsOnQuoteApplied) return "Quote rule already applied.";
        const ruleTypeText = RuleType.find(r => r.id == quoteData.billingType)?.type;
        
        return disableApplyQuoteButton ? `You cannot apply a total fee of $${quoteData.totalFee.toFixed(2)} because the invoice insurance rebates are higher than this at $${invoiceData.rebate.toFixed(2)}.` : `This action will apply ${ruleTypeText} of $${quoteData.calculationAmount.toFixed(2)}`;
    }

    const getMatchQuoteTooltip = () => {
        const type = quoteData.patientPaysTotalFee ? "total fee" : "gap fee";
        const amount = quoteData.patientPaysTotalFee ? quoteData.totalFee : quoteData.actualGap;

        return disableMatchQuoteButton ? `You cannot apply a total fee of $${quoteData.totalFee.toFixed(2)} because the invoice insurance rebates are higher than this at $${invoiceData.rebate.toFixed(2)}.` : `This action will set the ${type} to $${amount.toFixed(2)}, the same as the quote.`;
    }
   
</script>

{#if mode == 0}
    <div class="flex flex-col gap-y-4">
        <div class={`option-item `}>
            <div class="option-details">
                <div class="details-item">
                    {#if reason == 3}
                        {#if hasPaidPayment}
                            Patient has pre-paid{sumOfPaidPayments() > 0 ? ` \$${sumOfPaidPayments().toFixed(2)}` : ''}. Changing the patient amount to pay will result in a <b>TOP UP</b> payment of <b>${difference.toFixed(2)}</b> for the remainder being sent to the patient.
                        {:else}
                            {!quoteData.patientPaysTotalFee && quoteData.actualGap == 0 ? "" : `Patient requested to pre-pay but payment is still pending. Consider cancelling the quote and issuing the correct amount to pay in this invoice.`} Proceeding will result in a <b>TOP UP</b> payment of <b>${difference.toFixed(2)}</b> for the remainder being sent to the patient.
                        {/if}
                    {/if}
                    {#if reason == 4}
                        {#if hasPaidPayment}
                            Patient has pre-paid{sumOfPaidPayments() > 0 ? ` \$${sumOfPaidPayments().toFixed(2)}` : ''}. Changing the patient amount to pay will result in a <b>REFUND</b> payment of <b>${difference.toFixed(2)}</b> for the excess being created.
                        {:else}
                            {!quoteData.patientPaysTotalFee && quoteData.actualGap == 0 ? "" : `Patient requested to pre-pay but payment is still pending. Consider cancelling the quote and issuing the correct amount to pay in this invoice.`} Proceeding will result in a <b>REFUND</b> of <b>${difference.toFixed(2)}</b> being created
                        {/if}
                    {/if}
                </div>
            </div>
            <div class="flex flex-row space-between gap-4">
                {#if restoreDefaultInvoiceRule != null && !hideMatchQuoteButton}
                    <CustomTooltip
                        tooltipPosition="top"
                        contentClass="bg-[#fff] border-none"
                        contentStyle="box-shadow: 4px 4px 13.7px 1px rgba(0, 0, 0, 0.25);"
                    >
                        <!-- svelte-ignore a11y-click-events-have-key-events -->
                        <!-- svelte-ignore a11y-no-static-element-interactions -->
                        <Button
                            manualClass={`btn btn-sm btn-secondary `}
                            isDisabled={disableMatchQuoteButton}
                            clickFunc={() => {
                                if(!disableMatchQuoteButton) {
                                    restoreDefaultInvoiceRule();
                                }
                            }} slot="tooltipTrigger"
                            >
                            {" Match Quote Out of Pocket"}
                        </Button>

                        <p class="text-left cursor-text">
                            {getMatchQuoteTooltip()}
                        </p>
                    </CustomTooltip>
                {/if}
            </div>
        </div>
    </div>
{:else}
    {#if applyQuoteRule != null && !hideApplyQuoteButton && quoteData != null && quoteData.calculationAmount && quoteData.billingType}
        <CustomTooltip
            tooltipPosition="top"
            contentClass="bg-[#fff] border-none"
            contentStyle="box-shadow: 4px 4px 13.7px 1px rgba(0, 0, 0, 0.25);"
        >
            <!-- svelte-ignore a11y-click-events-have-key-events -->
            <!-- svelte-ignore a11y-no-static-element-interactions -->
            <Button
                manualClass={`btn btn-sm btn-secondary `}
                isDisabled={disableApplyQuoteButton}
                clickFunc={() => {
                    if(!disableApplyQuoteButton){
                        applyQuoteRule();
                    }
                }} slot="tooltipTrigger"
                >
                {"Apply Quote Rule"}
            </Button>

            <p class="text-left cursor-text">
                {getApplyQuoteTooltip()}
            </p>
        </CustomTooltip>
    {/if}
{/if}


<style>
   .option-item {
        width: 100%;
        border-radius: 6px;
        background-color: rgb(255 248 245);;
        border: 1px solid rgb(226, 93, 36);
        padding:15px;
        display: flex;
        flex-direction: column;
        gap: 10px;
   }

   .option-item:hover {
        cursor: pointer;
   }

    .option-details {
        display: flex;
        flex-direction: column;
        gap: 10px;
    }

    .details-item {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 10px;
        display: block;
    }

</style>
